/* eslint-disable @typescript-eslint/restrict-plus-operands */
import { useMemo, useState } from 'react';
import { ThemePalette } from 'mui.theme';
import { Treemap, ResponsiveContainer, Tooltip } from 'recharts';
import { Box, Grid, Typography } from '@mui/material';
import ProductionSelector from 'views/Production/components/Selector/Selector';
import MultiColorCircularLoader from 'views/Production/components/BoxLoader/MultiColorCircularLoader';
import type { DeviceStats } from 'types/utilization.types';
import { FlexBox } from 'components/Containers/FlexBox';
import { convertToTwoDecimalPlaces } from 'components/Charts/Tooltip';
import AssetItem from 'views/Utilization/AllFleet/Components/AssetItem';
import {
  getDataUnit,
  getColor,
  convertMinutesToHours,
  convertsecondsToHours,
  getUtilizationOptionLabel,
} from 'views/Utilization/utils';
import { truncateTo2dp } from 'utils/helpers/general';
import useAppTheme from 'hooks/useAppTheme';
import useCustomerInfo from 'hooks/useCustomerInfo';
import { useAppSelector } from 'store/hook';
import {
  commonListOptions,
  loaderListOptions,
  utilizationSortOptions,
} from 'views/Utilization/constants';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const _palette = clientThemePalette[CLIENT_THEME];

interface Props {
  isSelected: string;
  data: DeviceStats[];
  isLoaderType?: boolean;
  utilizationType: string;
}

const AssetView = ({ data, isLoaderType = false, utilizationType }: Props) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme ? _palette.dark : _palette.light;
  const { unitSystem } = useCustomerInfo();
  const isLoading = useAppSelector(
    (state) => state.utilizationReducer.loadingStates.isLoadingFleetAssets
  );

  const options = useMemo(() => {
    if (isLoaderType) {
      return loaderListOptions;
    }

    return commonListOptions;
  }, [isLoaderType]);

  const [selectedSource, setSelectedSource] = useState<string[]>([
    'Utilization',
  ]);
  const [selectedSort, setSelectedSort] = useState<string>('Most Utilization');

  const formattedData = data
    .map((item) => {
      const prodValue = Object.values(item.date_stats).reduce(
        (acc, stats) => acc + (stats.max_load_ton || 0),
        0
      );
      return {
        ...item,
        utilization: Number(item.avg_utilization_by_shift_hours) * 100,
        fuel: Number(item.total_fuel_consumption_l),
        avgfuelrate: +truncateTo2dp(item.avg_hourly_fuel_consumption_l),
        enginehours: Number(
          convertMinutesToHours(Number(item.total_engine_active_minutes))
        ),
        idlinghours: Number(
          convertsecondsToHours(Number(item.total_idling_duration))
        ),
        productivehours: Number(
          convertMinutesToHours(Number(item.total_engine_working_minutes))
        ),
        production: Number(truncateTo2dp(prodValue)),

        unit:
          selectedSource.length > 0
            ? getDataUnit(selectedSource[0], unitSystem)
            : '',
      };
    })
    .filter((item) => {
      return Number(item.enginehours) > 0;
    });

  const sortedListData = [...formattedData].sort((a: any, b: any) => {
    if (selectedSort === 'Least Utilization') {
      return a.utilization - b.utilization;
    }
    return b.utilization - a.utilization;
  });

  const sortedTreeMap = useMemo(() => {
    if (selectedSource.length === 0) return [];
    return sortedListData
      .filter(
        (item: any) => item[selectedSource[0].toLowerCase().replace(/ /g, '')]
      )
      .map((item, index, arr) => ({
        isMin:
          selectedSort === 'Least Utilization'
            ? index === 0
            : index === arr.length - 1,
        ...item,
      }));
  }, [sortedListData, selectedSource, selectedSort]);

  const noSelection = selectedSource.length === 0;

  return (
    <Box
      sx={{
        marginTop: '20px',
        padding: 2,
        backgroundColor: enableDarkTheme
          ? _palette.dark.boxBackground
          : _palette.light.boxBackground,
        borderRadius: '5px',
        border: `1px solid ${palette.borderGrey}`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: '600',
              paddingTop: '5px',
              color: palette.textSecondary,
            }}
          >
            {t('detailed_analysis')}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
          }}
        >
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: '600',
              color: palette.textSecondary,
              paddingRight: '15px',
              paddingTop: '5px',
            }}
          >
            {t('show_on_chart')}
          </Typography>
          <ProductionSelector
            options={options}
            selected={selectedSource}
            setSelected={setSelectedSource}
            minWidth="150px"
            multiple={true}
            getOptionLabel={(option) => getUtilizationOptionLabel(t, option)}
          />

          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: '600',
              color: palette.textSecondary,
              paddingLeft: '10px',
              paddingRight: '15px',
              paddingTop: '5px',
            }}
          >
            {t('sort_by_10')}
          </Typography>

          <ProductionSelector
            options={utilizationSortOptions}
            selected={selectedSort}
            setSelected={setSelectedSort}
            minWidth="150px"
            getOptionLabel={(option) => getUtilizationOptionLabel(t, option)}
          />
        </Box>
      </Box>

      {isLoading ? (
        <MultiColorCircularLoader height="200px" />
      ) : (
        <Grid
          container
          sx={{
            marginTop: '10px',
          }}
        >
          <Grid lg={12} sm={12} md={12}>
            <Box
              data-attribute="assetItemBox"
              sx={{
                backgroundColor: enableDarkTheme
                  ? _palette.dark.boxBackground
                  : _palette.light.boxBackground,
                paddingLeft: '5px',
              }}
            >
              <FlexBox
                sx={{
                  maxWidth: 'inherit',
                  height: '230px',
                  paddingTop: '20px',
                  marginRight: '10px',
                  paddingBottom: '10px',
                }}
              >
                {noSelection || sortedTreeMap.length === 0 ? (
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: palette.textPrimary,
                    }}
                  >
                    {t('no_data_available_for_the_selected_value')}
                  </Typography>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <Treemap
                      width={800}
                      height={600}
                      data={sortedTreeMap}
                      // Use the first selected metric for the treemap
                      dataKey={selectedSource[0]
                        .toLowerCase()
                        .replace(/ /g, '')}
                      stroke="#fff"
                      fill={ThemePalette.typography.lightGrey}
                      content={<CustomContent />}
                    >
                      <Tooltip
                        cursor={{ fill: 'rgba(0,0,0,0.1)' }}
                        wrapperStyle={{ outline: 'none', fontSize: '11px' }}
                        contentStyle={{
                          backgroundColor: 'transparent',
                        }}
                        itemStyle={{
                          color: enableDarkTheme
                            ? ThemePalette.typography.wheat
                            : ThemePalette.typography.black,
                        }}
                        content={
                          <CustomTooltip
                            selectedSource={
                              selectedSource.length > 0 ? selectedSource[0] : ''
                            }
                          />
                        }
                      />
                    </Treemap>
                  </ResponsiveContainer>
                )}
              </FlexBox>
              <Typography
                sx={{
                  fontSize: '12px',
                  color: palette.textPrimary,
                }}
              >
                {t('coloring_is_based_on_least_productive_asset')}
              </Typography>
            </Box>
          </Grid>
          <Grid lg={12} sm={12} md={12}>
            <Box
              sx={{
                marginTop: '15px',
              }}
            >
              {noSelection ? (
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: palette.textPrimary,
                  }}
                >
                  {t('no_data_available_for_the_selected_value')}
                </Typography>
              ) : sortedListData.length > 0 ? (
                <>
                  {sortedListData.map((item, index) => {
                    return (
                      <div key={index}>
                        <AssetItem
                          key={index}
                          data={item}
                          selectedSource={selectedSource}
                          utilizationType={utilizationType}
                          getSourceLabel={(source) =>
                            getUtilizationOptionLabel(t, source)
                          }
                        />
                      </div>
                    );
                  })}
                </>
              ) : null}
            </Box>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default AssetView;

const CustomContent = ({
  x,
  y,
  width,
  height,
  bumper_id: bumperId,
  value,
  unit,
  ...rest
}: any) => {
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={getColor(rest?.isMin)}
        stroke="#fff"
      />
      <text
        x={x + width / 2}
        y={y + height / 2}
        textAnchor="middle"
        fill="#fff"
        fontSize={14}
      >
        {bumperId}
      </text>
      <text
        x={x + width / 2}
        y={y + height / 2 + 20}
        textAnchor="middle"
        fill="#fff"
        fontSize={12}
      >
        {`${convertToTwoDecimalPlaces(value)} ${unit ?? ''}`}
      </text>
    </g>
  );
};

const CustomTooltip = ({ active, payload, selectedSource }: any) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  if (active && payload && payload.length) {
    const bumperId = payload[0].payload.bumper_id;
    const unit = payload[0].payload.unit;

    return (
      <div
        style={{
          background: 'transparent',
          color: enableDarkTheme
            ? ThemePalette.typography.wheat
            : ThemePalette.typography.black,
          fontSize: '11px',
          outline: 'none',
        }}
      >
        <p>{`Bumper Id: ${bumperId}`}</p>
        <p>
          {t('utilization_selected_source_value', {
            source: getUtilizationOptionLabel(t, selectedSource),
            value: `${truncateTo2dp(payload[0].value)}${unit}`,
          })}
        </p>
      </div>
    );
  }
  return null;
};
